import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

const OPEN_SKY_STATES_API = 'https://opensky-network.org/api/states/all';
const HEXDB_AIRCRAFT_API = 'https://hexdb.io/api/v1/aircraft/';
const PLANESPOTTERS_HEX_API = 'https://api.planespotters.net/pub/photos/hex/';

const DEFAULT_AIRPORT = {
  name: "Chicago O'Hare",
  icao: 'KORD',
  iata: 'ORD',
  lat: 41.9786,
  lon: -87.9048,
};

const MAJOR_US_AIRPORTS = [
  DEFAULT_AIRPORT,
  { name: 'Atlanta Hartsfield-Jackson', icao: 'KATL', iata: 'ATL', lat: 33.6407, lon: -84.4277 },
  { name: 'Los Angeles Intl', icao: 'KLAX', iata: 'LAX', lat: 33.9416, lon: -118.4085 },
  { name: 'Dallas/Fort Worth', icao: 'KDFW', iata: 'DFW', lat: 32.8998, lon: -97.0403 },
  { name: 'Denver Intl', icao: 'KDEN', iata: 'DEN', lat: 39.8561, lon: -104.6737 },
  { name: 'John F. Kennedy Intl', icao: 'KJFK', iata: 'JFK', lat: 40.6413, lon: -73.7781 },
  { name: 'Seattle Tacoma', icao: 'KSEA', iata: 'SEA', lat: 47.4502, lon: -122.3088 },
  { name: 'San Francisco Intl', icao: 'KSFO', iata: 'SFO', lat: 37.6213, lon: -122.3790 },
  { name: 'Las Vegas Harry Reid', icao: 'KLAS', iata: 'LAS', lat: 36.0840, lon: -115.1537 },
  { name: 'Miami Intl', icao: 'KMIA', iata: 'MIA', lat: 25.7959, lon: -80.2870 },
  { name: 'Phoenix Sky Harbor', icao: 'KPHX', iata: 'PHX', lat: 33.4342, lon: -112.0116 },
  { name: 'Houston George Bush', icao: 'KIAH', iata: 'IAH', lat: 29.9902, lon: -95.3368 },
  { name: 'Newark Liberty', icao: 'KEWR', iata: 'EWR', lat: 40.6895, lon: -74.1745 },
  { name: 'Boston Logan', icao: 'KBOS', iata: 'BOS', lat: 42.3656, lon: -71.0096 },
  { name: 'Washington National', icao: 'KDCA', iata: 'DCA', lat: 38.8512, lon: -77.0402 },
  { name: 'Washington Dulles', icao: 'KIAD', iata: 'IAD', lat: 38.9531, lon: -77.4565 },
  { name: 'Minneapolis St. Paul', icao: 'KMSP', iata: 'MSP', lat: 44.8848, lon: -93.2223 },
  { name: 'Detroit Metro', icao: 'KDTW', iata: 'DTW', lat: 42.2162, lon: -83.3554 },
  { name: 'Charlotte Douglas', icao: 'KCLT', iata: 'CLT', lat: 35.2144, lon: -80.9473 },
  { name: 'Philadelphia Intl', icao: 'KPHL', iata: 'PHL', lat: 39.8729, lon: -75.2437 },
];

const FALLBACK_TYPES = [
  'A220',
  'A320',
  'A321',
  'A330',
  'A350',
  'A380',
  'B737',
  'B747',
  'B757',
  'B767',
  'B777',
  'B787',
  'CRJ',
  'E190',
  'ERJ-175',
  'ATR-72',
  'DHC-8',
  'MD-11',
  'SSJ100',
];

const AIRLINER_CATEGORIES = new Set([0, 1, 3, 4, 5, 6]);

const isAirlinerType = (type) => {
  if (!type) return false;
  const normalized = String(type).toUpperCase();
  return (
    /^A\d{2,3}/.test(normalized) ||
    /^B\d{3,4}/.test(normalized) ||
    /^CRJ/.test(normalized) ||
    /^ERJ/.test(normalized) ||
    /^E\d{3}/.test(normalized) ||
    /^ATR/.test(normalized) ||
    /^DHC-?8/.test(normalized) ||
    /^MD-?\d/.test(normalized) ||
    /^DC-?\d/.test(normalized) ||
    /^FOKKER/.test(normalized) ||
    /^SSJ/.test(normalized)
  );
};

const shuffle = (items) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const buildChoices = (correctType, poolTypes) => {
  const unique = [...new Set(poolTypes.filter(Boolean))];
  const pool = unique.filter((type) => type !== correctType);
  const options = [correctType, ...pool.slice(0, 3)];

  if (options.length < 4) {
    const needed = 4 - options.length;
    const extras = FALLBACK_TYPES.filter((type) => !options.includes(type)).slice(
      0,
      needed
    );
    options.push(...extras);
  }

  return shuffle(options);
};

const toRadians = (value) => (value * Math.PI) / 180;

const distanceKm = (lat1, lon1, lat2, lon2) => {
  const earthRadius = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const buildBoundingBox = (lat, lon, radiusDeg = 1.2) => {
  const lamin = clamp(lat - radiusDeg, -90, 90);
  const lamax = clamp(lat + radiusDeg, -90, 90);
  const lomin = clamp(lon - radiusDeg, -180, 180);
  const lomax = clamp(lon + radiusDeg, -180, 180);
  return { lamin, lamax, lomin, lomax };
};

const getStateCategory = (state) => {
  if (!Array.isArray(state)) return null;
  const value = state[17];
  if (value === null || value === undefined) return null;
  return Number(value);
};

const isAirlinerCategory = (state) => {
  const category = getStateCategory(state);
  if (category === null || Number.isNaN(category)) return true;
  return AIRLINER_CATEGORIES.has(category);
};

const findNearestAirport = (lat, lon) => {
  return MAJOR_US_AIRPORTS.reduce((closest, airport) => {
    const distance = distanceKm(lat, lon, airport.lat, airport.lon);
    if (!closest || distance < closest.distance) {
      return { ...airport, distance };
    }
    return closest;
  }, null);
};

const normalizeAirportInput = (value) => String(value || '').trim().toUpperCase();

const findAirportByCode = (code) => {
  const normalized = normalizeAirportInput(code);
  if (!normalized) return null;
  return (
    MAJOR_US_AIRPORTS.find((airport) => airport.icao === normalized) ||
    MAJOR_US_AIRPORTS.find((airport) => airport.iata === normalized)
  );
};

const pickPlanespottersPhoto = (payload) => {
  if (!payload) return null;
  const photos = Array.isArray(payload.photos) ? payload.photos : payload?.data;
  if (!Array.isArray(photos) || photos.length === 0) return null;
  return photos[0];
};

function App() {
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selected, setSelected] = useState('');
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [round, setRound] = useState(0);
  const [airport, setAirport] = useState(DEFAULT_AIRPORT);
  const [locationStatus, setLocationStatus] = useState('idle');
  const [airportInput, setAirportInput] = useState('');
  const [isZoomed, setIsZoomed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem('planeQuizTheme');
    if (stored) return stored === 'dark';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });
  const [isAirportOpen, setIsAirportOpen] = useState(true);
  const photoWrapRef = useRef(null);
  const panRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);
  const dragState = useRef({
    active: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
    nextX: 0,
    nextY: 0,
  });

  const filteredPhotos = useMemo(() => {
    return photos.filter((photo) => isAirlinerType(photo.type));
  }, [photos]);

  const displayPhotos = filteredPhotos.length ? filteredPhotos : photos;
  const currentPhoto = displayPhotos[currentIndex];
  const choices = useMemo(() => {
    if (!currentPhoto) return [];
    const poolTypes = displayPhotos.map((photo) => photo.type);
    return buildChoices(currentPhoto.type, poolTypes);
  }, [currentPhoto, displayPhotos]);

  const fetchAircraftDetails = useCallback(async (icao24) => {
    try {
      const response = await fetch(`${HEXDB_AIRCRAFT_API}${icao24}`);
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      if (data?.status === '404') return null;
      return data;
    } catch (err) {
      return null;
    }
  }, []);

  const fetchPlanespottersPhoto = useCallback(async (icao24, registration, icaoType) => {
    try {
      const params = new URLSearchParams();
      if (registration) params.set('reg', registration);
      if (icaoType) params.set('icaoType', icaoType);
      const response = await fetch(
        `${PLANESPOTTERS_HEX_API}${icao24}?${params.toString()}`
      );
      if (!response.ok) return null;
      const data = await response.json();
      const photo = pickPlanespottersPhoto(data);
      if (!photo?.thumbnail_large?.src && !photo?.thumbnail?.src) return null;
      return {
        photo_url: photo.thumbnail_large?.src || photo.thumbnail?.src,
        page_link: photo.link,
        photographer: photo.photographer,
      };
    } catch (err) {
      return null;
    }
  }, []);

  const fetchPhotos = useCallback(async (selectedAirport) => {
    setStatus('loading');
    setError('');
    setSelected('');
    try {
      const bbox = buildBoundingBox(selectedAirport.lat, selectedAirport.lon);
      const response = await fetch(
        `${OPEN_SKY_STATES_API}?lamin=${bbox.lamin}&lomin=${bbox.lomin}&lamax=${bbox.lamax}&lomax=${bbox.lomax}&extended=1`
      );
      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }
      const data = await response.json();
      const states = Array.isArray(data?.states) ? data.states : [];
      const filteredStates = states.filter(isAirlinerCategory);
      if (!filteredStates.length) {
        throw new Error('No nearby aircraft found.');
      }

      const uniqueHex = Array.from(
        new Set(
          filteredStates
            .map((state) => state?.[0])
            .filter((icao24) => Boolean(icao24))
        )
      ).slice(0, 8);

      const enriched = await Promise.all(
        uniqueHex.map(async (icao24) => {
          const details = await fetchAircraftDetails(icao24);
          const state = filteredStates.find((entry) => entry?.[0] === icao24);
          const category = getStateCategory(state);
          if (!details?.Registration) return null;
          const registration = details.Registration;
          const icaoType = details.ICAOTypeCode || details.Type;
          const psPhoto = await fetchPlanespottersPhoto(
            icao24,
            registration,
            icaoType
          );
          if (!psPhoto) return null;

          return {
            id: icao24,
            type: details.ICAOTypeCode || details.Type || 'Unknown',
            serial: registration,
            taken_at: `${selectedAirport.name} (${selectedAirport.iata})`,
            category,
            ...psPhoto,
            source: 'Planespotters',
          };
        })
      );

      const usable = enriched.filter(Boolean);
      if (!usable.length) {
        throw new Error('No photos available for recent arrivals.');
      }

      setPhotos(usable);
      setCurrentIndex(0);
      setStatus('ready');
      setRound((prev) => prev + 1);
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Failed to load photos.');
    }
  }, [fetchAircraftDetails, fetchPlanespottersPhoto]);

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      return;
    }
    setLocationStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nearest = findNearestAirport(
          position.coords.latitude,
          position.coords.longitude
        );
        if (nearest) {
          setAirport(nearest);
          fetchPhotos(nearest);
          setLocationStatus('ready');
          return;
        }
        setLocationStatus('unavailable');
      },
      () => {
        setLocationStatus('denied');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  };

  const handleAirportSubmit = (event) => {
    event.preventDefault();
    const found = findAirportByCode(airportInput);
    if (!found) {
      setLocationStatus('invalid');
      return;
    }
    setLocationStatus('ready');
    setAirport(found);
    fetchPhotos(found);
  };

  const applyPan = useCallback((x, y, zoomValue) => {
    const node = photoWrapRef.current;
    if (!node) return;
    node.style.setProperty('--pan-x', `${x}px`);
    node.style.setProperty('--pan-y', `${y}px`);
    node.style.setProperty('--zoom', `${zoomValue}`);
  }, []);

  const resetPan = useCallback((zoomValue = 1) => {
    panRef.current = { x: 0, y: 0 };
    applyPan(0, 0, zoomValue);
  }, [applyPan]);

  useEffect(() => {
    fetchPhotos(DEFAULT_AIRPORT);
  }, [fetchPhotos]);

  useEffect(() => {
    const theme = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem('planeQuizTheme', theme);
  }, [isDarkMode]);

  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 700px)');
    if (!media) return undefined;
    const update = () => setIsAirportOpen(!media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    resetPan(isZoomed ? 1.6 : 1);
  }, [currentIndex, isZoomed, resetPan]);

  const handlePick = (choice) => {
    if (selected) return;
    setSelected(choice);
    if (choice === currentPhoto.type) {
      setScore((prev) => prev + 1);
      setStreak((prev) => prev + 1);
    } else {
      setStreak(0);
    }
  };

  const handleNext = () => {
    setSelected('');
    setCurrentIndex((prev) => {
      const nextIndex = prev + 1;
      if (nextIndex >= displayPhotos.length) {
        return 0;
      }
      return nextIndex;
    });
  };

  const handleSkip = () => {
    setSelected('');
    setStreak(0);
    handleNext();
  };

  const toggleZoom = () => {
    if (dragState.current.moved) {
      dragState.current.moved = false;
      return;
    }
    setIsZoomed((prev) => {
      const next = !prev;
      if (!next) {
        resetPan(1);
      }
      if (next) {
        applyPan(panRef.current.x, panRef.current.y, 1.6);
      }
      return next;
    });
  };

  const resetZoom = () => {
    dragState.current.moved = false;
    dragState.current.active = false;
    setIsZoomed(false);
    resetPan(1);
    setIsDragging(false);
  };

  const handlePointerDown = (event) => {
    if (!isZoomed) return;
    if (event.target?.closest?.('.photo-link, .photo-reset')) {
      return;
    }
    if (event.currentTarget?.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    dragState.current.active = true;
    dragState.current.moved = false;
    dragState.current.startX = event.clientX;
    dragState.current.startY = event.clientY;
    dragState.current.originX = panRef.current.x;
    dragState.current.originY = panRef.current.y;
    dragState.current.nextX = panRef.current.x;
    dragState.current.nextY = panRef.current.y;
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    if (!dragState.current.active || !isZoomed) return;
    const dx = event.clientX - dragState.current.startX;
    const dy = event.clientY - dragState.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragState.current.moved = true;
    }
    dragState.current.nextX = dragState.current.originX + dx;
    dragState.current.nextY = dragState.current.originY + dy;
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        panRef.current = {
          x: dragState.current.nextX,
          y: dragState.current.nextY,
        };
        applyPan(panRef.current.x, panRef.current.y, 1.6);
        rafRef.current = null;
      });
    }
  };

  const stopDragging = (event) => {
    if (event?.currentTarget?.releasePointerCapture) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (err) {
        // Ignore if pointer capture was not set.
      }
    }
    dragState.current.active = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setIsDragging(false);
  };

  return (
    <div className="app">
      <header className="hero">
        <div>
          <p className="eyebrow">Plane Spotter Quiz</p>
          <h1>Can you name this airliner?</h1>
          <p className="subtitle">
            Live aircraft near {airport.name} ({airport.iata}). Pick the aircraft
            type before your runway time runs out.
          </p>
        </div>
        <div className="stats">
          <div>
            <span className="label">Score</span>
            <span className="value">{score}</span>
          </div>
          <div>
            <span className="label">Streak</span>
            <span className="value">{streak}</span>
          </div>
          <div>
            <span className="label">Round</span>
            <span className="value">{round}</span>
          </div>
        </div>
        <button
          className="theme-toggle"
          type="button"
          onClick={() => setIsDarkMode((prev) => !prev)}
        >
          {isDarkMode ? 'Light mode' : 'Dark mode'}
        </button>
      </header>

      <details
        className="card airport-card"
        open={isAirportOpen}
        onToggle={(event) => setIsAirportOpen(event.currentTarget.open)}
      >
        <summary className="airport-summary">
          <div>
            <span className="eyebrow">Airport</span>
            <span className="summary-title">
              {airport.iata} · {airport.name}
            </span>
          </div>
          <span className="summary-action">
            {isAirportOpen ? 'Hide' : 'Change'}
          </span>
        </summary>
        <div className="airport-body">
          <p className="subtitle">
            Enter an IATA or ICAO code, or use your location to pick the nearest
            major U.S. airport. Default is Chicago O'Hare (ORD).
          </p>
          <form className="actions" onSubmit={handleAirportSubmit}>
            <input
              className="input"
              placeholder="Try ORD, LAX, JFK"
              value={airportInput}
              onChange={(event) => setAirportInput(event.target.value)}
              list="airport-codes"
            />
            <button className="primary" type="submit">
              Set airport
            </button>
            <button className="ghost" type="button" onClick={handleUseLocation}>
              Use my location
            </button>
          </form>
          <datalist id="airport-codes">
            {MAJOR_US_AIRPORTS.map((airportOption) => (
              <option
                key={airportOption.icao}
                value={airportOption.iata}
              >{`${airportOption.name} (${airportOption.iata})`}</option>
            ))}
          </datalist>
          {locationStatus === 'denied' && (
            <p className="result">Location permission denied. Using default.</p>
          )}
          {locationStatus === 'unavailable' && (
            <p className="result">Location not available in this browser.</p>
          )}
          {locationStatus === 'invalid' && (
            <p className="result">Airport code not found in the list.</p>
          )}
        </div>
      </details>

      <main className="content">
        {status === 'loading' && (
          <div className="card loading">
            <p>Fetching recent arrivals and photos…</p>
            <div className="loading-bar" />
          </div>
        )}

        {status === 'error' && (
          <div className="card error">
            <h2>We hit turbulence</h2>
            <p>{error}</p>
            <button className="primary" onClick={() => fetchPhotos(airport)}>
              Try again
            </button>
          </div>
        )}

        {status === 'ready' && currentPhoto && (
          <div className="quiz">
            <section className="photo-card">
              <div
                className={`photo-wrap ${isZoomed ? 'zoomed' : ''} ${
                  isDragging ? 'dragging' : ''
                }`}
                ref={photoWrapRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={stopDragging}
                onPointerCancel={stopDragging}
                onPointerLeave={stopDragging}
              >
                <img
                  src={currentPhoto.photo_url}
                  alt={`Airliner ${currentPhoto.type}`}
                  loading="lazy"
                  draggable={false}
                  onClick={toggleZoom}
                />
                {currentPhoto.page_link && (
                  <a
                    className="photo-link"
                    href={currentPhoto.page_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View on {currentPhoto.source || 'Planespotters'}
                  </a>
                )}
                {isZoomed && (
                  <button className="photo-reset" type="button" onClick={resetZoom}>
                    Reset zoom
                  </button>
                )}
              </div>
              <div className="photo-meta">
                <div>
                  <span className="label">Registration</span>
                  <span className="value">{currentPhoto.serial || 'Unknown'}</span>
                </div>
                <div>
                  <span className="label">Location</span>
                  <span className="value">{currentPhoto.taken_at || 'Unknown'}</span>
                </div>
                <div>
                  <span className="label">Photo Credit</span>
                  <div className="value">
                    {currentPhoto.photographer
                      ? `©\u00A0${currentPhoto.photographer}`
                      : 'Unknown photographer'}
                  </div>
                </div>
              </div>
            </section>

            <section className="choices">
              <h2>What aircraft type is this?</h2>
              <div className="grid">
                {choices.map((choice) => {
                  const isCorrect = selected && choice === currentPhoto.type;
                  const isWrong = selected && choice === selected && !isCorrect;
                  return (
                    <button
                      key={choice}
                      className={`choice ${isCorrect ? 'correct' : ''} ${
                        isWrong ? 'wrong' : ''
                      }`}
                      onClick={() => handlePick(choice)}
                    >
                      {choice}
                    </button>
                  );
                })}
              </div>
              <div className="actions">
                <button className="ghost" onClick={handleSkip}>
                  Skip
                </button>
                <button className="primary" onClick={handleNext}>
                  {selected ? 'Next airliner' : 'Reveal next'}
                </button>
              </div>
              {selected && (
                <p className="result">
                  {selected === currentPhoto.type
                    ? 'Nice catch! That is correct.'
                    : `Close! It was a ${currentPhoto.type}.`}
                </p>
              )}
            </section>
          </div>
        )}
      </main>

      <footer className="footer">
        <p>
          Nearby aircraft are sourced from OpenSky live state vectors. Aircraft
          details come from HexDB, and photos are Planespotters thumbnails with
          attribution and links to the original photo.
        </p>
        <button className="link" onClick={() => fetchPhotos(airport)}>
          Refresh the feed
        </button>
      </footer>
    </div>
  );
}

export default App;
